import './styles/tokens.css';
import './styles/base.css';
import { mountForm, type FormMode } from './ui/form.js';
import { mountGate } from './ui/gate.js';
import { mountHeader } from './ui/header.js';
import { mountCalendar } from './ui/calendar.js';
import { mountJobDetail } from './ui/jobDetail.js';
import { mountMore } from './ui/more.js';
import { mountReceipts } from './ui/receipts.js';
import { mountMoney } from './ui/money.js';
import { mountAnalytics } from './ui/analytics.js';
import { flushPendingJobs, resetSyncBackoff } from './sync/flush.js';
import { refreshReferenceCache } from './api/cache.js';
import {
  clearLocalUnlockSession,
  isLocallyUnlocked,
} from './auth/localUnlock.js';
import { initTheme } from './theme/theme.js';
import { getJob, type LocalJob } from './db/jobsDb.js';

type Tab = 'calendar' | 'receipts' | 'money' | 'analytics' | 'more';

const app = document.querySelector('#app');
if (!app) throw new Error('#app missing');

app.innerHTML = `
  <div id="gate-root" hidden></div>
  <div id="shell" hidden>
    <div id="header-root"></div>
    <main id="main" class="app-main">
      <div id="panel-calendar" class="tab-panel"></div>
      <div id="panel-receipts" class="tab-panel" hidden></div>
      <div id="panel-money" class="tab-panel" hidden></div>
      <div id="panel-analytics" class="tab-panel" hidden></div>
      <div id="panel-more" class="tab-panel" hidden></div>
    </main>
    <nav class="bottom-nav" role="tablist" aria-label="Main">
      <button type="button" role="tab" data-tab="calendar" aria-selected="true">Calendar</button>
      <button type="button" role="tab" data-tab="receipts" aria-selected="false">Receipts</button>
      <button type="button" role="tab" data-tab="money" aria-selected="false">Money</button>
      <button type="button" role="tab" data-tab="analytics" aria-selected="false">Analytics</button>
      <button type="button" role="tab" data-tab="more" aria-selected="false">More</button>
    </nav>
    <div id="sheet" class="sheet" hidden>
      <div class="sheet-backdrop" data-close-sheet></div>
      <div class="sheet-panel" role="dialog" aria-modal="true" aria-label="Job">
        <div class="sheet-head">
          <strong id="sheet-title">Entry</strong>
          <button type="button" class="icon-btn" data-close-sheet aria-label="Close">✕</button>
        </div>
        <div id="detail-root" hidden></div>
        <div id="form-root"></div>
      </div>
    </div>
  </div>
`;

const gateRoot = app.querySelector('#gate-root') as HTMLElement;
const shell = app.querySelector('#shell') as HTMLElement;
const headerRoot = app.querySelector('#header-root') as HTMLElement;
const panelCal = app.querySelector('#panel-calendar') as HTMLElement;
const panelReceipts = app.querySelector('#panel-receipts') as HTMLElement;
const panelMoney = app.querySelector('#panel-money') as HTMLElement;
const panelAnalytics = app.querySelector('#panel-analytics') as HTMLElement;
const panelMore = app.querySelector('#panel-more') as HTMLElement;
const formRoot = app.querySelector('#form-root') as HTMLElement;
const detailRoot = app.querySelector('#detail-root') as HTMLElement;
const sheet = app.querySelector('#sheet') as HTMLElement;
const sheetTitle = app.querySelector('#sheet-title') as HTMLElement;

const panels: Record<Tab, HTMLElement> = {
  calendar: panelCal,
  receipts: panelReceipts,
  money: panelMoney,
  analytics: panelAnalytics,
  more: panelMore,
};

function showTab(tab: Tab) {
  (Object.keys(panels) as Tab[]).forEach((t) => {
    panels[t].hidden = t !== tab;
  });
  document.querySelectorAll('.bottom-nav [data-tab]').forEach((el) => {
    const btn = el as HTMLButtonElement;
    btn.setAttribute('aria-selected', String(btn.dataset.tab === tab));
  });
}

document.querySelectorAll('.bottom-nav [data-tab]').forEach((el) => {
  el.addEventListener('click', () => {
    showTab((el as HTMLElement).dataset.tab as Tab);
  });
});

type FormApi = Awaited<ReturnType<typeof mountForm>>;
type DetailApi = Awaited<ReturnType<typeof mountJobDetail>>;
type MoreApi = Awaited<ReturnType<typeof mountMore>>;
type HeaderApi = Awaited<ReturnType<typeof mountHeader>>;
type CalApi = Awaited<ReturnType<typeof mountCalendar>>;
type ReceiptsApi = Awaited<ReturnType<typeof mountReceipts>>;
type MoneyApi = Awaited<ReturnType<typeof mountMoney>>;
type AnalyticsApi = Awaited<ReturnType<typeof mountAnalytics>>;

let form!: FormApi;
let detail!: DetailApi;
let more!: MoreApi;
let header!: HeaderApi;
let calendar!: CalApi;
let receipts!: ReceiptsApi;
let money!: MoneyApi;
let analytics!: AnalyticsApi;
let gateApi: ReturnType<typeof mountGate> | null = null;
let shellMounted = false;

function openSheet(opts: { date: string; mode: FormMode; title: string }) {
  detailRoot.hidden = true;
  formRoot.hidden = false;
  detail.clear();
  form.clearEdit();
  sheetTitle.textContent = opts.title;
  form.setWorkDate(opts.date);
  form.setMode(opts.mode);
  sheet.hidden = false;
  form.focus();
}

async function openJobSheet(id: string) {
  formRoot.hidden = true;
  detailRoot.hidden = false;
  form.clearEdit();
  sheetTitle.textContent = 'Job';
  sheet.hidden = false;
  await detail.open(id);
}

async function openEditSheet(job: LocalJob) {
  detailRoot.hidden = true;
  formRoot.hidden = false;
  detail.clear();
  sheetTitle.textContent = 'Edit job';
  sheet.hidden = false;
  await form.loadJob(job);
  form.focus();
}

function closeSheet() {
  sheet.hidden = true;
  detailRoot.hidden = true;
  formRoot.hidden = false;
  detail.clear();
  form.clearEdit();
  form.setMode('planned');
}

sheet.querySelectorAll('[data-close-sheet]').forEach((el) => {
  el.addEventListener('click', () => closeSheet());
});

async function refreshAll() {
  const tasks: Promise<unknown>[] = [
    header.refresh(),
    calendar.refresh(),
    more.refresh(),
  ];
  if (shellMounted) {
    tasks.push(receipts.refresh());
    tasks.push(money.refresh());
    tasks.push(analytics.refresh());
  }
  await Promise.all(tasks);
}

async function runSync() {
  const cached = await refreshReferenceCache();
  if (cached.ok && shellMounted) {
    await form.refreshLists();
  }
  await flushPendingJobs();
  await refreshAll();
}

async function afterAuth() {
  resetSyncBackoff();
  await runSync();
}

function showGate() {
  shell.hidden = true;
  gateRoot.hidden = false;
  if (!gateApi) {
    gateApi = mountGate(gateRoot, {
      onUnlocked: () => {
        void enterApp();
      },
    });
  }
  gateApi.focus();
}

async function mountShell() {
  if (shellMounted) return;
  shellMounted = true;

  form = await mountForm(formRoot, {
    onSaved: () => {
      closeSheet();
      void refreshAll();
      void runSync();
    },
    onError: () => {},
  });

  detail = await mountJobDetail(detailRoot, {
    onEdit: (job) => {
      void openEditSheet(job);
    },
    onDeleted: () => {
      closeSheet();
      void refreshAll();
      void runSync();
    },
    onClose: () => closeSheet(),
    onOpenReceipt: (id) => {
      closeSheet();
      showTab('receipts');
      void receipts.open(id);
    },
  });

  more = await mountMore(panelMore, {
    onSignedIn: () => {
      void afterAuth();
    },
    onSignedOut: () => {
      void clearLocalUnlockSession().then(() => showGate());
    },
    onSync: () => {
      resetSyncBackoff();
      void runSync();
    },
    onChange: () => {
      void refreshAll();
      void runSync();
    },
  });

  header = await mountHeader(headerRoot, {
    onOpenSettings: () => {
      showTab('more');
      more.showSettings();
    },
    onSync: () => {
      resetSyncBackoff();
      void runSync();
    },
    onOpenMoneyOverdue: () => {
      showTab('money');
      money.showOverdue();
    },
    onOpenRenewal: (id) => {
      showTab('more');
      more.showCompliance(id);
    },
    onOpenSyncSettings: () => {
      showTab('more');
      more.showSettings();
    },
    onOpenCalendarUnconfirmed: () => {
      showTab('calendar');
      calendar.focusUnconfirmed();
    },
    onOpenWeekSignoff: (weekStart) => {
      showTab('calendar');
      calendar.focusWeek(weekStart);
    },
  });

  calendar = await mountCalendar(panelCal, {
    onBookDay: (iso) => {
      openSheet({
        date: iso,
        mode: 'planned',
        title: 'Add booking',
      });
    },
    onLogDay: (iso) => {
      openSheet({
        date: iso ?? new Date().toISOString().slice(0, 10),
        mode: 'planned',
        title: 'Log a day',
      });
    },
    onEditJob: (id) => {
      void (async () => {
        const job = await getJob(id);
        if (job) await openEditSheet(job);
      })();
    },
    onOpenJob: (id) => {
      void openJobSheet(id);
    },
    onChange: () => {
      void refreshAll();
      void runSync();
    },
  });

  receipts = await mountReceipts(panelReceipts, {
    onChange: () => {
      void refreshAll();
      void runSync();
    },
    onOpenJob: (id) => {
      showTab('calendar');
      void openJobSheet(id);
    },
  });

  money = await mountMoney(panelMoney, {
    onChange: () => {
      void refreshAll();
      void runSync();
    },
  });
  analytics = await mountAnalytics(panelAnalytics);

  window.addEventListener('online', () => {
    resetSyncBackoff();
    void runSync();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !shell.hidden) void runSync();
  });
}

async function enterApp() {
  gateRoot.hidden = true;
  shell.hidden = false;
  await mountShell();
  await afterAuth();
}

async function boot() {
  await initTheme();

  if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.register('/sw.js').catch(() => {});
  }

  if (await isLocallyUnlocked()) {
    await enterApp();
  } else {
    showGate();
  }
}

boot().catch((err) => {
  const msg = err instanceof Error ? err.message : String(err);
  gateRoot.hidden = false;
  shell.hidden = true;
  gateRoot.innerHTML = `
    <section class="auth-gate" role="alert">
      <div class="auth-gate-card">
        <h1>Couldn’t start</h1>
        <p>${msg.replace(/[<>&]/g, '')}</p>
        <p class="muted">Pull to refresh, or clear this site’s data and reopen.</p>
      </div>
    </section>`;
});
